import { Box, CircularProgress, Link, Typography } from '@mui/material';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import Locale from '/b2b/common/components/Locale';
import MessageBubble from '../MessageBubble';
import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import moment from 'moment';
import styles from './MessageList.styles';

const useStyles = makeStyles(styles);

/*
 * `id` identifies the event, `conversationId` is what the link opens. Under chat-api the
 * two differ: the event is an escalation, the target the conversation it created.
 */
const EscalationMarker = ({ event, classes, onOpenConversation }) => (
    <Box className={classes.escalationMarker}>
        <Box className={classes.escalationMarkerLine} />
        <Box className={classes.escalationMarkerContent}>
            <Typography variant="body2" fontWeight="bold" component="span" color="inherit">
                <Locale path={event.label} />
            </Typography>
            <Typography variant="body2" component="span" color="inherit">
                {moment(event.createdAt).format('L')}
            </Typography>
            {/* An escalation can exist before its conversation does: no target, no link. */}
            {event.linkText && event.conversationId && onOpenConversation && (
                <Link
                    component="button"
                    type="button"
                    className={classes.escalationMarkerLink}
                    onClick={() => onOpenConversation(event.conversationId)}
                >
                    <Typography variant="body2" fontWeight="bold" component="span" color="inherit">
                        <Locale path={event.linkText} />
                    </Typography>
                </Link>
            )}
        </Box>
        <Box className={classes.escalationMarkerLine} />
    </Box>
);
EscalationMarker.propTypes = {
    event: PropTypes.shape({
        id: PropTypes.string,
        conversationId: PropTypes.string,
        label: PropTypes.string.isRequired,
        createdAt: PropTypes.string.isRequired,
        linkText: PropTypes.string,
    }).isRequired,
    classes: PropTypes.object.isRequired,
    onOpenConversation: PropTypes.func,
};

/*
 * The conversation's own state, drawn the way the escalation lifecycle notices chat-api
 * used to write into the transcript were. Pinned last rather than placed by timestamp:
 * it describes where the conversation stands now, not something that happened at a point
 * in it.
 */
const SystemNotice = ({ children, classes }) => (
    <Box className={classes.systemNotice}>
        <Box className={classes.systemNoticeLine} />
        <Typography variant="caption" className={classes.systemNoticeText}>
            {children}
        </Typography>
        <Box className={classes.systemNoticeLine} />
    </Box>
);
SystemNotice.propTypes = {
    children: PropTypes.node.isRequired,
    classes: PropTypes.object.isRequired,
};

const MessageList = props => {
    const {
        messages,
        isStreaming,
        onFeedback,
        onLoadEarlier,
        hasEarlierMessages,
        isLoadingEarlierMessages,
        escalationEvents = [],
        stateNoticeKey,
        onOpenConversation,
        onNavigate,
    } = props;
    const classes = useStyles(props);
    const bottomRef = useRef(null);
    const topSentinelRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const isInitialLoad = useRef(true);
    const prevMessageCountRef = useRef(messages.length);
    const scrollHeightBeforePrepend = useRef(null);
    const wasPrependRef = useRef(false);

    // Restore scroll position after prepend so the viewport stays anchored
    useLayoutEffect(() => {
        const prevCount = prevMessageCountRef.current;
        const newCount = messages.length;

        if (newCount > prevCount && scrollHeightBeforePrepend.current !== null) {
            const container = scrollContainerRef.current;
            if (container) {
                const addedHeight = container.scrollHeight - scrollHeightBeforePrepend.current;
                container.scrollTop += addedHeight;
            }
            scrollHeightBeforePrepend.current = null;
            wasPrependRef.current = true;
        }

        prevMessageCountRef.current = newCount;
    }, [messages.length]);

    // Scroll to bottom when the message count grows, streaming state changes, escalation
    // events are added, or the state notice changes. Intentionally uses messages.length rather
    // than messages so that reference-only updates (e.g. silent poll refreshes) don't yank the
    // user's scroll position.
    useEffect(() => {
        if (wasPrependRef.current) {
            wasPrependRef.current = false;
            return;
        }

        if (bottomRef.current) {
            bottomRef.current.scrollIntoView(
                isInitialLoad.current ? { behavior: 'instant' } : { behavior: 'smooth' }
            );
            isInitialLoad.current = false;
        }
    }, [messages.length, isStreaming, escalationEvents, stateNoticeKey]);

    // Trigger load-earlier when the top sentinel scrolls into view
    useEffect(() => {
        if (!hasEarlierMessages || isLoadingEarlierMessages || !topSentinelRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    const container = scrollContainerRef.current;
                    if (container) {
                        scrollHeightBeforePrepend.current = container.scrollHeight;
                    }
                    if (onLoadEarlier) onLoadEarlier();
                }
            },
            { root: scrollContainerRef.current, threshold: 0 }
        );

        observer.observe(topSentinelRef.current);
        return () => observer.disconnect();
    }, [hasEarlierMessages, isLoadingEarlierMessages, onLoadEarlier]);

    // Inline "Submitted to privacy team" markers are interleaved into the
    // message list via mergedItems — sort messages and escalation events
    // together by timestamp so markers land at the right point in the thread.
    const mergedItems = useMemo(() => {
        const messageItems = messages.map(m => ({ _type: 'message', ...m }));
        const eventItems = escalationEvents.map(e => ({ _type: 'escalation', ...e }));
        return [...messageItems, ...eventItems].sort((a, b) => {
            const ta = a.timestamp || a.createdAt || '';
            const tb = b.timestamp || b.createdAt || '';
            return ta < tb ? -1 : ta > tb ? 1 : 0;
        });
    }, [messages, escalationEvents]);

    if (messages.length === 0) {
        return null;
    }

    // An escalation marker is never the streaming tail, so it can't hold the spinner.
    const lastMessageIndex = mergedItems.reduce(
        (last, item, i) => (item._type === 'message' ? i : last),
        -1
    );

    return (
        <Box className={classes.root} ref={scrollContainerRef}>
            <Box className={classes.messages}>
                {hasEarlierMessages && (
                    <Box className={classes.loadEarlierWrapper} ref={topSentinelRef}>
                        {isLoadingEarlierMessages && <CircularProgress size={20} />}
                    </Box>
                )}
                {mergedItems.map((item, index) => {
                    if (item._type === 'escalation') {
                        return (
                            <EscalationMarker
                                key={item.id}
                                event={item}
                                classes={classes}
                                onOpenConversation={onOpenConversation}
                            />
                        );
                    }
                    return (
                        <MessageBubble
                            key={item.stableId || item.id}
                            messageId={item.id}
                            role={item.role}
                            content={item.content}
                            isStreaming={index === lastMessageIndex && isStreaming}
                            initialFeedback={item.feedback}
                            citations={item.citations}
                            onFeedback={onFeedback}
                            timestamp={item.timestamp || item.createdAt}
                            author={item.author}
                            isEmployee={item.isEmployee}
                            onNavigate={onNavigate}
                        />
                    );
                })}
                {stateNoticeKey && (
                    <SystemNotice classes={classes}>
                        <Locale path={stateNoticeKey} />
                    </SystemNotice>
                )}
                <div ref={bottomRef} />
            </Box>
        </Box>
    );
};

MessageList.propTypes = {
    messages: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            role: PropTypes.string.isRequired,
            content: PropTypes.string.isRequired,
            isEmployee: PropTypes.bool,
        })
    ).isRequired,
    isStreaming: PropTypes.bool,
    onFeedback: PropTypes.func,
    onLoadEarlier: PropTypes.func,
    hasEarlierMessages: PropTypes.bool,
    isLoadingEarlierMessages: PropTypes.bool,
    escalationEvents: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            conversationId: PropTypes.string,
            createdAt: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            linkText: PropTypes.string,
        })
    ),
    stateNoticeKey: PropTypes.string,
    onOpenConversation: PropTypes.func,
    onNavigate: PropTypes.func,
};

export default MessageList;
